import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { Room } from "building-types/rooms/roomTypes";
import type { Table } from "common/data/table";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { planarGraphAdjacentFace, planarGraphHalfEdgeToLine2 } from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2EdgeCount, polyLine2EdgeTangent, polyLine2Reverse } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2Dot } from "geom/vec2";

export class RoomIndexImpl implements RoomIndex {
  constructor(
    readonly rooms: Table<Room>,
    readonly roomLevelIndices: Table<number>,
    readonly roomsByLevel: Table<string[]>,
    readonly planarGraphs: Table<PolygonPlanarGraph>,
  ) {}

  traverseRooms(
    currentRoom: Room,
    traversalPredicate: (room: Room, adjRoom: Room) => boolean,
    traversalFunc: (room: Room) => void,
    visited: { [key: number]: boolean } = {},
  ) {
    const { roomLevelIndices } = this;

    if (!currentRoom || visited[roomLevelIndices[currentRoom.id]]) {
      return;
    }

    traversalFunc(currentRoom);
    visited[roomLevelIndices[currentRoom.id]] = true;

    for (const adjRoom of this.adjacentRoomsFlattened(currentRoom)) {
      if (traversalPredicate(currentRoom, adjRoom)) {
        this.traverseRooms(adjRoom, traversalPredicate, traversalFunc, visited);
      }
    }
  }

  adjacentRooms(room: Room): Room[][] {
    const {
      outline: roomOutline,
      level: { id: levelId },
    } = room;

    const levelRooms = roomsOnLevel(levelId, this.rooms, this.roomsByLevel);
    const roomIndexOnLevel = this.roomLevelIndices[room.id];
    const planarGraph = this.planarGraphs[levelId];

    const rooms: Room[][] = [];

    // for each wall of room
    for (let wallIndex = 0, wallCount = polyLine2EdgeCount(roomOutline); wallIndex < wallCount; wallIndex++) {
      const subwalls = planarGraph.polygonToPlanarGraph[roomIndexOnLevel][wallIndex];

      const roomsOnWall: Room[] = [];
      rooms.push(roomsOnWall);

      // for each sub-wall of wall
      for (let subWallIndex = 0, subWallCount = subwalls.length; subWallIndex < subWallCount; subWallIndex++) {
        const { index, edgeIndex } = subwalls[subWallIndex];

        const adjFace = planarGraphAdjacentFace(planarGraph.planarGraph, index, edgeIndex);
        const polygonMap = planarGraph.planarGraphToPolygon[adjFace];

        const adjRoom: Room | undefined =
          polygonMap.length !== 0 && polygonMap[0] ? levelRooms[polygonMap[0].index] : undefined;
        if (!adjRoom) {
          continue;
        }
        roomsOnWall.push(adjRoom);
      }
    }

    return rooms;
  }

  adjacentRoomOnEdge(room: Room, roomEdgeIndex: number, subEdgeIndex: number): Room | undefined {
    const {
      level: { id: levelId },
    } = room;

    const levelRooms = roomsOnLevel(levelId, this.rooms, this.roomsByLevel);
    const roomIndexOnLevel = this.roomLevelIndices[room.id];
    const planarGraph = this.planarGraphs[levelId];

    const { index, edgeIndex } = planarGraph.polygonToPlanarGraph[roomIndexOnLevel][roomEdgeIndex][subEdgeIndex];

    const adjFace = planarGraphAdjacentFace(planarGraph.planarGraph, index, edgeIndex);
    const polygonMap = planarGraph.planarGraphToPolygon[adjFace];

    return polygonMap.length !== 0 && polygonMap[0] ? levelRooms[polygonMap[0].index] : undefined;
  }

  adjacentRoomsFlattened(room: Room): Room[] {
    const rooms = this.adjacentRooms(room);

    const finalRooms: Room[] = [];

    for (const roomSet of rooms) {
      for (const room of roomSet) {
        if (room) {
          finalRooms.push(room);
        }
      }
    }

    return finalRooms;
  }

  roomEdgeLine2s(room: Room): PolyLine2[][] {
    const {
      outline: roomOutline,
      level: { id: levelId },
    } = room;

    const roomIndexOnLevel = this.roomLevelIndices[room.id];
    const planarGraph = this.planarGraphs[levelId];

    const edges: PolyLine2[][] = [];

    // for each wall of room
    for (let wallIndex = 0, wallCount = polyLine2EdgeCount(roomOutline); wallIndex < wallCount; wallIndex++) {
      const subwalls = planarGraph.polygonToPlanarGraph[roomIndexOnLevel][wallIndex];

      const edgesOnWall: PolyLine2[] = [];
      edges.push(edgesOnWall);

      // for each sub-wall of wall
      for (let subWallIndex = 0, subWallCount = subwalls.length; subWallIndex < subWallCount; subWallIndex++) {
        const { index, edgeIndex } = subwalls[subWallIndex];
        const halfEdgeIndex = planarGraph.planarGraph.faceHalfEdges[index][edgeIndex];

        const line = planarGraphHalfEdgeToLine2(planarGraph.planarGraph, halfEdgeIndex);

        // realign the edge with the room outline
        const [tx, ty] = polyLine2EdgeTangent(roomOutline, 0);
        const [hetx, hety] = polyLine2EdgeTangent(line, 0);

        if (vec2Dot(tx, ty, hetx, hety) > 0) {
          polyLine2Reverse(line);
        }

        edgesOnWall.push(line);
      }
    }

    return edges;
  }
}
